import { observer } from 'mobx-react'
import styles from './BuyPopup.module.less'
import { Popup } from 'antd-mobile'
import classNames from 'classnames'
import wechatpay from '@/assets/images/wechat-pay.png'
import alipay from '@/assets/images/ali-pay.png'
import close from '@/assets/images/close.png'
import { useEffect, useRef, useState } from 'react'
import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { userService } from '@/services/userService/userService'
import { payService } from '@/services/payService'
import { CheckCircleFill } from 'antd-mobile-icons'

export interface BuyPopupProps {
  goodsId?: string
  goodsType?: DataModels.goods.GoodsType
  visible: boolean
  onClose?: () => void
  onDownload?: () => void
  onPaySuccess?: () => void
}

let isPaying = false
const BuyPopup = observer((props: BuyPopupProps) => {
  const countdownTimerRef = useRef<number | null>(null)
  const [checked, setChecked] = useState(1)
  const [chargeChannel, setChargeChannel] = useState<'wx_pub_qr' | 'alipay_qr'>('wx_pub_qr')
  const [priceInfo, setPriceInfo] = useState<DataModels.goods.CourseMaterialPriceInfo[]>([])
  
  // 新增优惠券相关状态
  const [coupons, setCoupons] = useState<DataModels.Coupon.UseCouponItem[]>([])
  const [selectedCoupon, setSelectedCoupon] = useState<DataModels.Coupon.UseCouponItem | null>(null)
  const [showCouponPanel, setShowCouponPanel] = useState(false)
  const [currentTimestamp, setCurrentTimestamp] = useState(Date.now())

  useEffect(() => {
    const initPlanData = async () => {
      if (!props.goodsId || !props.goodsType) return
      const { data } = await api.goods.getCourseMaterialPriceInfo(props.goodsId, props.goodsType)
      setPriceInfo(data)
      // setChecked(data.length)
    }
    initPlanData()
  }, [props.goodsId, props.goodsType])

  useEffect(() => {
    if (!props.visible) {
      return undefined
    }

    setCurrentTimestamp(Date.now())

    countdownTimerRef.current = window.setInterval(() => {
      setCurrentTimestamp(Date.now())
    }, 1000)

    return () => {
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current)
        countdownTimerRef.current = null
      }
    }
  }, [props.visible])

  // 获取可用优惠券
  useEffect(() => {
    const fetchCoupons = async () => {
      if (!props.goodsId || priceInfo.length === 0) return
      try {
        const currentGoods = priceInfo[checked - 1]
        if (!currentGoods?.goodsId) return
        
        const { data } = await api.order.getAvaliableCoupons(currentGoods.goodsId)
        setCoupons(data.list || [])
        
        // // 如果有优惠券且未选择，自动选择第一张
        if (data.list?.length > 0 && !selectedCoupon) {
          setSelectedCoupon(data.list[0])
        }
      } catch (error) {
        console.error('获取优惠券失败:', error)
      }
    }
  
    fetchCoupons()
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.goodsId, props.goodsType, priceInfo, checked,selectedCoupon?.id])

  const parseCurrency = (price?: number) => {
    if (typeof price === 'number') {
      return commonUtils.parseCurrency(price)
    }
    return '-'
  }

  const isSingleCourse = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    return item?.goodsType === 1
  }

  const hasLimitedPromotion = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item || !isSingleCourse(item) || !item.priceLine) {
      return false
    }

    return true
  }

  const getDisplayPrice = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item) {
      return 0
    }

    return item.goodsPrice
  }

  const getOriginalPrice = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item || !hasLimitedPromotion(item)) {
      return undefined
    }

    return item.priceLine
  }

  const getCountdownSeconds = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item?.promotionEndTime) {
      return 0
    }

    return Math.max(
      0,
      Math.floor((new Date(item.promotionEndTime).getTime() - currentTimestamp) / 1000)
    )
  }

  const formatCountdown = (totalSeconds: number) => {
    const days = Math.floor(totalSeconds / (24 * 3600))
    const hours = Math.floor(totalSeconds / 3600)
    const remainHours = Math.floor((totalSeconds % (24 * 3600)) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0) {
      return `${days}天${String(remainHours).padStart(2, '0')}时${String(minutes).padStart(
        2,
        '0'
      )}分${String(seconds).padStart(2, '0')}秒`
    }

    return `${String(hours).padStart(2, '0')}时${String(minutes).padStart(2, '0')}分${String(
      seconds
    ).padStart(2, '0')}秒`
  }

  // 计算优惠后的价格
  const calculateFinalPrice = () => {
    const originalPrice = getDisplayPrice(priceInfo[checked - 1])
    if (selectedCoupon) {
      return Math.max(0, originalPrice  - selectedCoupon.rule.reductAmount )
    }
    return originalPrice 
  }

  // 处理优惠券选择
  const handleCouponSelect = (coupon: DataModels.Coupon.UseCouponItem) => {
    setSelectedCoupon(coupon)
    setShowCouponPanel(false)
  }

  useEffect(() => {
    const currentItem = priceInfo[checked - 1]
    if (!props.visible || !countdownTimerRef.current || !currentItem?.promotionEndTime) {
      return
    }

    const countdown = Math.max(
      0,
      Math.floor((new Date(currentItem.promotionEndTime).getTime() - currentTimestamp) / 1000)
    )

    if (countdown === 0) {
      window.clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [checked, currentTimestamp, priceInfo, props.visible])

  const doPay = async () => {
   
    if (isPaying) {
      return
    }
    userService.postUserActionRecord({
      module: '移动端官网',
      function: '付费弹窗',
      position: '付费弹窗点击购买按钮',
      action: '点击',
      element: '按钮',
    })
    isPaying = true

    const currentGoods = priceInfo[checked - 1]
    await payService.createH5Pay({
      goodsId: currentGoods?.goodsId,
      goodsType: currentGoods?.goodsType,
      chargeChannel,
      userCouponId: selectedCoupon?.id,
      onPaySuccess: () => {
        props.onClose?.()
        props.onPaySuccess?.()
      },
    })
     selectedCoupon && setSelectedCoupon(null) // 切换到单课件时清除已选优惠券
    isPaying = false
  }

  const onClose = () => {
    props.onClose?.()
  }

  return (
    <Popup
      visible={props.visible}
      onMaskClick={onClose}
      bodyStyle={{
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
      }}
    >
      <div className={styles.buyPopup}>
        <div className={styles.buyPopupHeader}>
          <div>下载课程及教案</div>
          <div onClick={onClose} className={styles.buyPopupHeaderClose}>
            <img src={close} draggable={false} />
          </div>
        </div>
        <div className={styles.buyPopupContent}>
          <div className={styles.buyPopupUser}>
            <div className={styles.infoBox}>
              <div className={styles.infoBoxLabel}>账号：</div>
              <div className={styles.infoBoxVal}>{userService.userId}</div>
            </div>
          </div>
          <div className={styles.buyPopupCourse}>
            <div
              className={classNames(styles.goodsBox, {
                [styles.checked]: checked === 1,
              })}
              onClick={() => {
                setChecked(1);
                selectedCoupon && setSelectedCoupon(null) // 切换到单课件时清除已选优惠券
              }}
            >
              <div className={styles.goodsBoxTitle}>单课件</div>
              <div className={styles.goodsBoxGift}>
                <span>
                  <img src={commonUtils.getStaticFile('pay/1.png')} alt='pay/1' /> 赠送教学设计(部分科目包含)
                </span>
              </div>
              {hasLimitedPromotion(priceInfo[0]) ? (
                <div className={classNames(styles.goodsBoxPrice, styles.limitedPriceBox)}>
                  <div className={styles.limitedPriceLine}>
                    <div className={styles.limitedPriceTag}>限时价</div>
                    <div className={styles.limitedPriceValue}>
                      <span className={styles.unit}>￥</span>
                      {parseCurrency(getDisplayPrice(priceInfo[0]))}
                    </div>
                  </div>
                  <div className={styles.originalPriceText}>
                    原价 ¥{parseCurrency(getOriginalPrice(priceInfo[0]))}
                  </div>
                
                </div>
              ) : (
                <div className={styles.goodsBoxPrice}>
                  <span className={styles.unit}>￥</span>
                  {parseCurrency(priceInfo[0]?.goodsPrice)}
                </div>
              )}
                <div className={styles.countdownText}>
                    {getCountdownSeconds(priceInfo[0]) > 0
                      ? `距结束${formatCountdown(getCountdownSeconds(priceInfo[0]))}`
                      : ''}
                  </div>
            </div>
            {priceInfo.length > 1 && (
              <div
                className={classNames(styles.goodsBox, styles.volume, {
                  [styles.checked]: checked === 2,
                })}
                onClick={() => {setChecked(2); selectedCoupon && setSelectedCoupon(null)}}
              >
                <div className={styles.goodsBoxTitle}>{priceInfo[1]?.goodsName}</div>
                <div className={styles.goodsBoxGift}>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/2' /> 包含
                    <span className={styles.highlight}>{priceInfo[1]?.couresCount}</span>
                    套精编课件
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/2' />
                    赠送教学设计（部分科目包含）
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/2' />
                    赠送配套音视频素材/班会主题课件
                  </span>
                </div>
                <div className={styles.goodsBoxPrice}>
                  <span className={styles.unit}>￥</span>
                  {parseCurrency(priceInfo[1]?.goodsPrice)}
                  <div className={styles.onePrice}>
                    单课件仅需
                    <span className={styles.highlight}>
                      {parseCurrency(priceInfo[1]?.onePrice)}
                    </span>
                    元
                  </div>
                </div>
              </div>
            )}
            {priceInfo.length > 2 && (
              <div
                className={classNames(styles.goodsBox, styles.grade, {
                  [styles.checked]: checked === 3,
                })}
                onClick={() => {setChecked(3); selectedCoupon && setSelectedCoupon(null)}}
              >
                <div className={styles.goodsBoxTitle}>{priceInfo[2]?.goodsName}</div>
                <div className={styles.goodsBoxGift}>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/3.png')} alt='pay/3' /> 包含
                    <span className={styles.highlight}>{priceInfo[2]?.couresCount}</span>
                    套课件
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/3.png')} alt='pay/2' />
                    赠送教学设计（部分科目包含）
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/3.png')} alt='pay/3' />
                    赠送配套音视频素材
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/3.png')} alt='pay/3' />
                    赠送班会主题课件
                  </span>
                  {/* <span>
                    <img src={commonUtils.getStaticFile('pay/3.png')} alt='pay/3' />
                    赠送班会主题课件
                  </span> */}
                  <span>
                    <img src={commonUtils.getStaticFile('pay/3.png')} alt='pay/3' />
                    专属人工客服答疑
                  </span>
                </div>
                <div className={styles.goodsBoxPrice}>
                  <span className={styles.unit}>￥</span>
                  {parseCurrency(priceInfo[2]?.goodsPrice)}
                  <div className={styles.onePrice}>
                    单课件仅需
                    <span className={styles.highlight}>
                      {parseCurrency(priceInfo[2]?.onePrice)}
                    </span>
                    元
                  </div>
                </div>
              </div>
            )}
            {priceInfo.length > 3 && (
              <div
                className={classNames(styles.goodsBox, styles.schoolSection, {
                  [styles.checked]: checked === 4,
                })}
                onClick={() => setChecked(4)}
              >
                <div className={styles.goodsBoxTitle}>{priceInfo[3]?.goodsName}</div>
                <div className={styles.goodsBoxGift}>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/3' /> 包含
                    <span className={styles.highlight}>{priceInfo[3]?.couresCount}</span>
                    套课件
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/2' />
                    赠送教学设计（部分科目包含）
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/3' />
                    赠送配套音视频素材
                  </span>
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/3' />
                    赠送班会主题课件
                  </span>
                  {/* <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/3' /> 
                    赠送教辅资料
                  </span> */}
                  <span>
                    <img src={commonUtils.getStaticFile('pay/2.png')} alt='pay/3' />
                    专属人工客服答疑
                  </span>
                </div>
                <div className={styles.goodsBoxPrice}>
                  <span className={styles.unit}>￥</span>
                  {parseCurrency(priceInfo[3]?.goodsPrice)}
                  <div className={styles.onePrice}>
                    单课件仅需
                    <span className={styles.highlight}>
                      {parseCurrency(priceInfo[3]?.onePrice)}
                    </span>
                    元
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 优惠券模块 - 折叠卡片形式 */}
          {coupons.length > 0 && (
            <div className={styles.couponSection}>
              <div
                className={styles.couponCard}
                onClick={() => setShowCouponPanel(!showCouponPanel)}
              >
                {selectedCoupon && (
                  <div className={styles.selectedCornerMark}>
                    ✓ 已选
                  </div>
                )}
                <div className={styles.couponCardContent}>
                  {selectedCoupon ? (
                    // 已选状态 - 电商卡片样式
                    <div className={styles.selectedCouponContent}>
                      <div className={styles.couponLeft}>
                        <div className={styles.couponAmountLarge}>
                          <span className={styles.yuan}>¥</span>
                          {parseCurrency(selectedCoupon.rule.reductAmount)}
                        </div>
                        <div className={styles.couponConditionSmall}>
                          满{parseCurrency(selectedCoupon.rule.minOrderPrice)}可用
                        </div>
                      </div>
                      <div className={styles.couponMiddle}>
                        <div className={styles.couponItemName}>{selectedCoupon.name}</div>
                        <div className={styles.couponItemDesc}>
                          {selectedCoupon.type}
                        </div>
                      </div>
                      {/* <div className={styles.couponRight}>
                        <div
                          className={styles.removeButton}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveCoupon()
                          }}
                        >
                          移除
                        </div>
                      </div> */}
                    </div>
                  ) : (
                    // 未选状态
                    <div className={styles.unselectedCouponContent}>
                      <div className={styles.couponInfo}>
                        <div className={styles.couponMainText}>
                          可用优惠券 ({coupons.length}张)
                        </div>
                        <div className={styles.couponSubText}>
                          最高省¥{parseCurrency(Math.max(...coupons.map(c => c.rule.reductAmount)))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={classNames(styles.couponArrow, {
                  [styles.couponArrowExpanded]: showCouponPanel
                })}>
                  ›
                </div>
              </div>

              {/* 优惠券选择面板 - 直接内嵌展开 */}
              {showCouponPanel && (
                <div className={styles.couponListWrapper}>
                  {coupons.map((coupon) => (
                    <div
                      key={coupon.id}
                      className={classNames(styles.couponItem, {
                        [styles.couponItemSelected]: selectedCoupon?.id === coupon.id,
                      })}
                      onClick={() => handleCouponSelect(coupon)}
                    >
                      <div className={styles.couponLeft}>
                        <div className={styles.couponAmountLarge}>
                          <span className={styles.yuan}>¥</span>
                          {parseCurrency(coupon.rule.reductAmount)}
                        </div>
                        <div className={styles.couponConditionSmall}>
                          满{parseCurrency(coupon.rule.minOrderPrice)}可用
                        </div>
                      </div>
                      <div className={styles.couponMiddle}>
                        <div className={styles.couponItemName}>{coupon.name}</div>
                        <div className={styles.couponItemDesc}>
                          {coupon.type}
                        </div>
                        <div className={styles.couponValidity}>
                          有效期：{new Date(coupon.startTime).toLocaleDateString('zh-CN')} 至{' '}
                          {new Date(coupon.endTime).toLocaleDateString('zh-CN')}
                        </div>
                      </div>
                      <div className={styles.couponRight}>
                        {selectedCoupon?.id === coupon.id ? (
                          <div className={styles.selectedMark}>✓ </div>
                        ) : (
                          <div className={styles.useButton}>使用</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={classNames(styles.infoBox, styles.payItemBox)}>
            <div
              className={classNames(styles.payTypeItem)}
              onClick={() => {setChargeChannel('wx_pub_qr');selectedCoupon && setSelectedCoupon(null)}}
            >
              <div>
                <img className={styles.icon} src={wechatpay} />
                微信支付
              </div>
              {chargeChannel === 'wx_pub_qr' ? (
                <CheckCircleFill className={styles.checkIcon} color='#FF7006' />
              ) : (
                <div className={styles.checkIcon1} />
              )}
            </div>
            {!commonUtils.isWechat() && (
              <div
                className={classNames(styles.payTypeItem)}
                onClick={() => {setChargeChannel('alipay_qr');selectedCoupon && setSelectedCoupon(null)}}
              >
                <div>
                  <img className={styles.icon} src={alipay} />
                  支付宝支付
                </div>
                {chargeChannel === 'alipay_qr' ? (
                  <CheckCircleFill className={styles.checkIcon} color='#FF7006' />
                ) : (
                  <div className={styles.checkIcon1} />
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.buyPopupFooter}>
          <div className={styles.buyPopupFooterLeft}>
            <div className={styles.payBarLeftLabel}>应付：</div>
            <div className={styles.payBarLeftVal}>
              <span>￥</span>
              {parseCurrency(calculateFinalPrice())}
              {getOriginalPrice(priceInfo[checked - 1]) && (
                <span className={styles.originalPrice}>
                   ¥{parseCurrency(getOriginalPrice(priceInfo[checked - 1]))}
                </span>
              )}
               {selectedCoupon && priceInfo[checked - 1]?.goodsType!==1 && (
                <span className={styles.originalPrice}>
                  ¥{parseCurrency(priceInfo[checked - 1]?.goodsPrice)}
                </span>
              )}
            </div>
          </div>
          {/* {selectedCoupon && (
            <div className={styles.saveTag}>
              省¥{parseCurrency(selectedCoupon.rule.reductAmount)}
            </div>
          )} */}
          <div
            className={styles.payBtn}
            onClick={() => {
              // 购买
              doPay()
            }}
          >
            购买
          </div>
        </div>
      </div>
    </Popup>
  )
})

export default BuyPopup
